<template>
  <div class="ivr-status-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader pageTitle="IVR System Status" />
      <main class="page-content">

        <!-- Page Header -->
        <div class="page-header">
          <div class="header-content">
            <h1>IVR System Status</h1>
            <p class="subtitle">Connectivity between your FreePBX server and Quali-D.</p>
          </div>
          <button class="btn-secondary" @click="load" :disabled="loading">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>
            Refresh
          </button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="error-state">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
          <div>
            <div>{{ error }}</div>
            <button class="retry-btn" @click="load">Retry</button>
          </div>
        </div>

        <div v-else-if="status" class="status-content">

          <!-- Main Connection Card -->
          <div class="connection-card" :class="connectionClass">
            <div class="connection-left">
              <div class="connection-icon" :class="connectionClass">
                <svg v-if="connectionState === 'connected'" width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
                <svg v-else-if="connectionState === 'stale'" width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                <svg v-else-if="connectionState === 'disconnected'" width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M23.64 7c-.45-.34-4.93-4-11.64-4-1.5 0-2.89.19-4.15.48L18.18 13.8 23.64 7zm-6.6 8.22L3.27 1.44 2 2.72l2.05 2.06C1.91 5.76.59 6.82.36 7L12 21.5l4-5 2.64 2.64 1.27-1.27-2.87-2.65z"/></svg>
                <svg v-else width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              </div>
              <div>
                <div class="connection-state-label" :class="connectionClass">{{ connectionLabel }}</div>
                <div class="connection-detail">{{ connectionDetail }}</div>
              </div>
            </div>
            <div class="connection-badge" :class="connectionClass">
              {{ connectionLabel }}
            </div>
          </div>

          <!-- Two columns of check items -->
          <div class="status-sections">

            <!-- FreePBX → Quali-D -->
            <div class="status-section">
              <div class="section-heading">
                <div class="section-arrow">
                  <span class="section-from">FreePBX</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
                  <span class="section-to">Quali-D</span>
                </div>
              </div>
              <div class="check-list">
                <div class="check-item" :class="status.pbx_connected ? 'check-ok' : 'check-fail'">
                  <div class="check-icon">
                    <svg v-if="status.pbx_connected" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                  </div>
                  <div class="check-content">
                    <div class="check-title">Heartbeat received</div>
                    <div class="check-sub">
                      <span v-if="status.last_heartbeat_minutes_ago !== null && status.last_heartbeat_minutes_ago !== undefined">
                        {{ status.last_heartbeat_minutes_ago === 0 ? 'just now' : status.last_heartbeat_minutes_ago + ' min ago' }}
                      </span>
                      <span v-else-if="status.last_heartbeat">{{ formatDate(status.last_heartbeat) }}</span>
                      <span v-else>Never received</span>
                    </div>
                  </div>
                </div>

                <div class="check-item" :class="status.extension_count > 0 ? 'check-ok' : 'check-warn'">
                  <div class="check-icon">
                    <svg v-if="status.extension_count > 0" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                  </div>
                  <div class="check-content">
                    <div class="check-title">Extensions synced</div>
                    <div class="check-sub">
                      <span v-if="status.extension_count > 0">{{ status.extension_count }} extension{{ status.extension_count !== 1 ? 's' : '' }}
                        <span v-if="status.last_sync">, last at {{ formatTime(status.last_sync) }}</span>
                      </span>
                      <span v-else>No extensions synced yet</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quali-D → FreePBX -->
            <div class="status-section">
              <div class="section-heading">
                <div class="section-arrow">
                  <span class="section-from">Quali-D</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
                  <span class="section-to">FreePBX (AGI)</span>
                </div>
              </div>
              <div class="check-list">
                <div class="check-item" :class="activeFlows > 0 ? 'check-ok' : 'check-warn'">
                  <div class="check-icon">
                    <svg v-if="activeFlows > 0" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                  </div>
                  <div class="check-content">
                    <div class="check-title">IVR flows active</div>
                    <div class="check-sub">
                      <span v-if="activeFlows > 0">{{ activeFlows }} flow{{ activeFlows !== 1 ? 's' : '' }} published</span>
                      <span v-else>No flows active yet</span>
                    </div>
                  </div>
                </div>

                <div class="check-item check-info">
                  <div class="check-icon check-icon-info">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                  </div>
                  <div class="check-content">
                    <div class="check-title">AGI secret registered</div>
                    <div class="check-sub">Configured during FreePBX module setup</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="status-footer">
            <span>Last updated: {{ lastUpdated }}</span>
            <router-link to="/extensions" class="link-btn">View Extensions</router-link>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { IVRService } from '@/services/ivrService'

export default {
  name: 'IVRStatusView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      status: null,
      activeFlows: 0,
      loading: true,
      error: null,
      lastUpdated: '—'
    }
  },

  computed: {
    connectionState () {
      if (!this.status) return 'unknown'
      if (!this.status.last_heartbeat) return 'never'
      if (this.status.pbx_connected) return 'connected'
      const mins = this.status.last_heartbeat_minutes_ago
      if (mins !== null && mins !== undefined && mins <= 60) return 'stale'
      return 'disconnected'
    },

    connectionClass () {
      const map = {
        connected: 'state-connected',
        stale: 'state-stale',
        disconnected: 'state-disconnected',
        never: 'state-never'
      }
      return map[this.connectionState] || 'state-never'
    },

    connectionLabel () {
      const map = {
        connected: 'Connected',
        stale: 'Idle',
        disconnected: 'Disconnected',
        never: 'Not set up'
      }
      return map[this.connectionState] || 'Unknown'
    },

    connectionDetail () {
      if (this.connectionState === 'connected') {
        const mins = this.status.last_heartbeat_minutes_ago
        return mins === 0 ? 'Last seen just now' : `Last seen ${mins} min ago`
      }
      if (this.connectionState === 'stale') {
        const mins = this.status.last_heartbeat_minutes_ago
        return `Last seen ${mins} min ago — may be disconnected`
      }
      if (this.connectionState === 'disconnected') {
        return 'FreePBX is not responding'
      }
      return 'FreePBX module not configured'
    }
  },

  async mounted () {
    await this.load()
  },

  methods: {
    async load () {
      this.loading = true
      this.error = null
      try {
        const [statusRes, flowsRes] = await Promise.all([
          IVRService.getConnectionStatus(),
          IVRService.getFlows()
        ])
        this.status = statusRes
        const flows = flowsRes.flows || []
        this.activeFlows = flows.filter(f => f.status === 'active').length
        this.lastUpdated = new Date().toLocaleTimeString()
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },

    formatDate (str) {
      if (!str) return '—'
      return new Date(str).toLocaleString()
    },

    formatTime (str) {
      if (!str) return '—'
      return new Date(str).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  }
}
</script>

<style scoped>
.ivr-status-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}
.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}
.page-content {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
}

/* Page Header */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 28px;
}
.header-content h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px;
}
.subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

/* Loading / Error */
.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}
.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-primary);
  border-top-color: #3B82F6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.error-state {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 12px;
  padding: 18px 20px;
  color: #DC2626;
  font-size: 14px;
}
.retry-btn {
  margin-top: 8px;
  padding: 6px 14px;
  border: 1px solid #FECACA;
  border-radius: 6px;
  background: #FEF2F2;
  color: #DC2626;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}
.retry-btn:hover { background: #FEE2E2; }

/* Connection Card */
.connection-card {
  border-radius: 16px;
  padding: 24px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  border: 2px solid;
}
.connection-card.state-connected {
  background: rgba(16,185,129,0.05);
  border-color: rgba(16,185,129,0.25);
}
.connection-card.state-stale {
  background: rgba(245,158,11,0.05);
  border-color: rgba(245,158,11,0.25);
}
.connection-card.state-disconnected {
  background: rgba(239,68,68,0.05);
  border-color: rgba(239,68,68,0.25);
}
.connection-card.state-never {
  background: var(--bg-tertiary);
  border-color: var(--border-primary);
}

.connection-left {
  display: flex;
  align-items: center;
  gap: 18px;
}
.connection-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.connection-icon.state-connected { background: rgba(16,185,129,0.12); color: #10B981; }
.connection-icon.state-stale { background: rgba(245,158,11,0.12); color: #F59E0B; }
.connection-icon.state-disconnected { background: rgba(239,68,68,0.12); color: #EF4444; }
.connection-icon.state-never { background: var(--bg-secondary); color: var(--text-secondary); }

.connection-state-label {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 4px;
}
.connection-state-label.state-connected { color: #059669; }
.connection-state-label.state-stale { color: #D97706; }
.connection-state-label.state-disconnected { color: #DC2626; }
.connection-state-label.state-never { color: var(--text-secondary); }

.connection-detail {
  font-size: 14px;
  color: var(--text-secondary);
}

.connection-badge {
  padding: 6px 18px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.connection-badge.state-connected { background: rgba(16,185,129,0.12); color: #059669; }
.connection-badge.state-stale { background: rgba(245,158,11,0.12); color: #D97706; }
.connection-badge.state-disconnected { background: rgba(239,68,68,0.12); color: #DC2626; }
.connection-badge.state-never { background: var(--bg-secondary); color: var(--text-secondary); border: 1px solid var(--border-primary); }

/* Status Sections */
.status-sections {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 24px;
}
@media (max-width: 768px) {
  .status-sections { grid-template-columns: 1fr; }
}

.status-section {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  overflow: hidden;
}

.section-heading {
  padding: 14px 20px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-primary);
}
.section-arrow {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
}
.section-from { color: var(--text-primary); }
.section-arrow svg { color: var(--text-secondary); }
.section-to { color: #3B82F6; }

.check-list {
  padding: 8px 0;
}
.check-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--border-primary);
}
.check-item:last-child { border-bottom: none; }

.check-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}
.check-ok .check-icon { background: rgba(16,185,129,0.1); color: #10B981; }
.check-fail .check-icon { background: rgba(239,68,68,0.1); color: #EF4444; }
.check-warn .check-icon { background: rgba(245,158,11,0.1); color: #F59E0B; }
.check-icon-info { background: rgba(59,130,246,0.1); color: #3B82F6; }

.check-content { flex: 1; min-width: 0; }
.check-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 3px;
}
.check-sub { font-size: 13px; color: var(--text-secondary); }

/* Footer */
.status-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  font-size: 13px;
  color: var(--text-secondary);
}
.link-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #3B82F6;
  text-decoration: none;
  transition: color 0.2s;
}
.link-btn:hover { color: #2563EB; }

/* Buttons */
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  transition: all 0.2s;
}
.btn-secondary:hover:not(:disabled) { background: var(--bg-tertiary); }
.btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
